import { useMemo, useState } from 'react'
import EmptyState from '../../components/shell/EmptyState'
import {
  COVERAGE_REPORT_STATUS_LABEL,
  fillRateFor,
  fillRateLabel,
  filterReportRows,
  sortReportRows,
  summarizeByDate,
  toReportRows,
  totalsFor,
  type CoverageReportColumn,
  type CoverageReportFilters,
  type CoverageReportRow,
  type CoverageReportSort,
  type CoverageReportStatus,
} from './coverageReport'
import { AVAILABLE_THRESHOLD_PERCENT, belowAvailableThreshold } from './manpowerFigures'
import { useCoverageData } from './useCoverageData'
import WorkbookExport from './WorkbookExport'
import { detailedExportTable, summaryExportTable } from './coverageReportExport'
import { exportExcel, exportPdf, type ExportTable } from './reportExport'
import ReportControls from './ReportControls'
import { coverageFilters } from './coverageFilters'
import { useReportRange } from './useReportRange'

/**
 * docs/product/reports.md's daily coverage report: the coverage view by
 * date, shift, assignment, and team, with a row of totals above it. The
 * page chrome (header, navigation) belongs to the role's own wrapper; this is
 * the report itself, shared by every role that reads it.
 */
const STATUS_CLASS: Record<CoverageReportStatus, string> = {
  understaffed: 'text-error',
  'missing-qualification': 'text-error',
  'at-risk': 'text-warning',
  'adequately-staffed': 'text-success',
  overstaffed: 'text-info',
  'no-target': 'text-ink-muted',
}

function dateLabel(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

const DETAIL_COLUMNS: { id: CoverageReportColumn; label: string; numeric?: boolean }[] = [
  { id: 'date', label: 'Date' },
  { id: 'team', label: 'Team' },
  { id: 'shift', label: 'Shift' },
  { id: 'assignment', label: 'Assignment' },
  { id: 'required', label: 'Required', numeric: true },
  { id: 'scheduled', label: 'Scheduled', numeric: true },
  { id: 'available', label: 'Available', numeric: true },
  { id: 'fillPercent', label: 'Filled %', numeric: true },
  { id: 'onLeave', label: 'On leave', numeric: true },
  { id: 'detailedOut', label: 'Detailed out', numeric: true },
  { id: 'unfilledRole', label: 'Unfilled role' },
  { id: 'qualificationGap', label: 'Qualification gap' },
  { id: 'status', label: 'Status' },
]

const HEADER_CLASS = 'px-4 py-2.5 text-caption font-bold tracking-wide text-ink-subtle uppercase'
const CELL_CLASS = 'px-4 py-2.5 text-small [font-variant-numeric:tabular-nums]'

// A filled share under the threshold reads red, and says so to assistive technology.
function PercentValue({ percent }: { percent: number | null }) {
  if (percent === null) return <>None</>
  return (
    <>
      {percent}%
      {belowAvailableThreshold(percent) && <span className="sr-only"> (below {AVAILABLE_THRESHOLD_PERCENT}%)</span>}
    </>
  )
}

const percentClass = (percent: number | null) => (belowAvailableThreshold(percent) ? 'font-bold text-error' : '')

function Tile({ label, value, note, warn }: { label: string; value: number | string; note?: string; warn?: boolean }) {
  return (
    <div className="rounded-card border border-line bg-canvas p-4">
      <p className="text-caption font-bold tracking-wide text-ink-subtle uppercase">{label}</p>
      <p className={`mt-1 text-h2 font-extrabold [font-variant-numeric:tabular-nums] ${warn ? 'text-error' : ''}`}>{value}</p>
      {warn && <span className="sr-only">Below {AVAILABLE_THRESHOLD_PERCENT}%</span>}
      {note && <p className="mt-0.5 text-caption text-ink-muted">{note}</p>}
    </div>
  )
}

export default function DailyCoverageReport() {
  const range = useReportRange()
  const { from, to } = range
  const [filters, setFilters] = useState<Omit<CoverageReportFilters, 'from' | 'to'>>({})
  const [view, setView] = useState<'summary' | 'detailed'>('summary')
  const [sort, setSort] = useState<CoverageReportSort>({ column: 'date', direction: 'asc' })
  const { rows, lookups, loading, failed } = useCoverageData(from, to)
  const [exportNotice, setExportNotice] = useState<{ kind: 'done' | 'failed'; text: string } | null>(null)

  const reportRows = useMemo(() => toReportRows(rows, lookups), [rows, lookups])
  const filtered = useMemo(() => filterReportRows(reportRows, { from, to, ...filters }), [reportRows, from, to, filters])
  const sorted = useMemo(() => sortReportRows(filtered, sort), [filtered, sort])
  const totals = useMemo(() => totalsFor(filtered), [filtered])
  const summary = useMemo(() => summarizeByDate(filtered), [filtered])
  const fillRate = useMemo(() => fillRateFor(filtered), [filtered])
  const understaffed = filtered.filter((row) => row.status === 'understaffed' && row.assignment !== 'Whole shift').length

  const reportFilters = coverageFilters(reportRows, lookups, filters, setFilters, ['team', 'supervisor', 'shift', 'assignment', 'status'])

  function toggleSort(column: CoverageReportColumn) {
    setSort((current) =>
      current.column === column ? { column, direction: current.direction === 'asc' ? 'desc' : 'asc' } : { column, direction: 'asc' },
    )
  }

  function ariaSort(column: CoverageReportColumn): 'ascending' | 'descending' | 'none' {
    if (sort.column !== column) return 'none'
    return sort.direction === 'asc' ? 'ascending' : 'descending'
  }

  const rangeInvalid = range.invalid

  async function runExport(format: 'pdf' | 'xlsx') {
    const input = { from, to, filters, lookups, view, generatedAt: new Date() }
    const table: ExportTable =
      view === 'summary' ? summaryExportTable(summary, totals, input, filtered) : detailedExportTable(sorted, totals, input)
    const fileName = `muster-daily-coverage-${from}-to-${to}.${format}`
    try {
      if (format === 'pdf') await exportPdf(table, fileName)
      else await exportExcel(table, fileName)
      setExportNotice({ kind: 'done', text: `Exported ${fileName}` })
    } catch {
      setExportNotice({ kind: 'failed', text: 'The export could not be created. Try again.' })
    }
  }

  const canExport = !loading && !failed && !rangeInvalid && (view === 'summary' ? summary.length : sorted.length) > 0

  return (
    <div className="flex flex-col gap-5">
      <section aria-label="Totals" className="grid grid-cols-2 gap-3 lg:grid-cols-6">
        <Tile label="Headcount filled" value={fillRateLabel(fillRate)} warn={belowAvailableThreshold(fillRate.percent)} note={fillRate.required === 0 ? undefined : `${fillRate.filled} of ${fillRate.required} required`} />
        <Tile label="Required" value={totals.required} />
        <Tile label="Available" value={totals.available} />
        <Tile label="On leave" value={totals.onLeave} />
        <Tile label="Detailed out" value={totals.detailedOut} />
        <Tile label="Understaffed" value={understaffed} />
      </section>

      <ReportControls range={range} filters={reportFilters} onReset={() => setFilters({})} />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div role="group" aria-label="Report view" className="flex">
          {(['summary', 'detailed'] as const).map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={view === option}
              onClick={() => setView(option)}
              className={`min-h-11 border border-line-strong px-4 text-small font-bold first:rounded-l-control last:rounded-r-control ${
                view === option ? 'border-primary bg-selected' : 'hover:bg-surface-2'
              }`}
            >
              {option === 'summary' ? 'Summary' : 'Detailed'}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <p role="status" className="text-small text-ink-muted">
            {exportNotice?.kind === 'done'
              ? exportNotice.text
              : loading
                ? 'Loading'
                : `${view === 'summary' ? summary.length : sorted.length} ${view === 'summary' ? 'days' : 'rows'}`}
          </p>
          <button
            type="button"
            disabled={!canExport}
            onClick={() => runExport('pdf')}
            className="min-h-11 rounded-control border border-line-strong px-4 text-small font-bold hover:bg-surface-2 disabled:opacity-50"
          >
            Export PDF
          </button>
          <button
            type="button"
            disabled={!canExport}
            onClick={() => runExport('xlsx')}
            className="min-h-11 rounded-control border border-line-strong px-4 text-small font-bold hover:bg-surface-2 disabled:opacity-50"
          >
            Export Excel
          </button>
          <WorkbookExport />
        </div>
      </div>
      {exportNotice?.kind === 'failed' && (
        <p role="alert" className="text-small text-error">
          {exportNotice.text}
        </p>
      )}

      {failed ? (
        <p role="alert" className="rounded-card border border-line bg-canvas p-4 text-small text-error">
          The coverage report could not be loaded. Reload the page, or try again in a moment.
        </p>
      ) : (
        <div
          role="region"
          aria-label="Daily coverage"
          tabIndex={0}
          className="overflow-x-auto rounded-card border border-line bg-canvas"
        >
          {loading ? (
            <div aria-hidden="true" className="flex flex-col gap-2 p-4">
              {[0, 1, 2, 3].map((line) => (
                <div key={line} className="h-8 animate-pulse rounded-control bg-surface-2" />
              ))}
            </div>
          ) : view === 'summary' ? (
            <SummaryTable summary={summary} />
          ) : (
            <DetailTable rows={sorted} ariaSort={ariaSort} onSort={toggleSort} />
          )}
        </div>
      )}
    </div>
  )
}

function SummaryTable({ summary }: { summary: ReturnType<typeof summarizeByDate> }) {
  if (summary.length === 0) {
    return <EmptyState>No coverage for these dates. Choose another pay period or clear a filter.</EmptyState>
  }
  return (
    <table className="w-full min-w-[720px] border-collapse">
      <caption className="sr-only">Daily coverage, one row per date</caption>
      <thead>
        <tr className="border-b border-line bg-surface-2 text-left">
          <th scope="col" className={HEADER_CLASS}>Date</th>
          {['Required', 'Available', 'On leave', 'Detailed out', 'Unfilled roles', 'Qualification gaps', 'Filled %'].map((label) => (
            <th key={label} scope="col" className={`${HEADER_CLASS} text-right`}>{label}</th>
          ))}
          <th scope="col" className={HEADER_CLASS}>Status</th>
        </tr>
      </thead>
      <tbody>
        {summary.map((day) => (
          <tr key={day.date} className="border-b border-line last:border-b-0">
            <th scope="row" className={`${CELL_CLASS} text-left font-semibold`}>{dateLabel(day.date)}</th>
            <td className={`${CELL_CLASS} text-right`}>{day.required}</td>
            <td className={`${CELL_CLASS} text-right`}>{day.available}</td>
            <td className={`${CELL_CLASS} text-right`}>{day.onLeave}</td>
            <td className={`${CELL_CLASS} text-right`}>{day.detailedOut}</td>
            <td className={`${CELL_CLASS} text-right`}>{day.unfilledRoles}</td>
            <td className={`${CELL_CLASS} text-right`}>{day.qualificationGaps}</td>
            <td className={`${CELL_CLASS} text-right ${percentClass(day.fillRate.percent)}`}><PercentValue percent={day.fillRate.percent} /></td>
            <td className={`${CELL_CLASS} font-semibold ${STATUS_CLASS[day.status]}`}>{COVERAGE_REPORT_STATUS_LABEL[day.status]}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function DetailTable({
  rows,
  ariaSort,
  onSort,
}: {
  rows: CoverageReportRow[]
  ariaSort: (column: CoverageReportColumn) => 'ascending' | 'descending' | 'none'
  onSort: (column: CoverageReportColumn) => void
}) {
  if (rows.length === 0) {
    return <EmptyState>No coverage for these dates. Choose another pay period or clear a filter.</EmptyState>
  }
  return (
    <table className="w-full min-w-[1200px] border-collapse">
      <caption className="sr-only">Daily coverage by date, shift, assignment, and team</caption>
      <thead>
        <tr className="border-b border-line bg-surface-2">
          {DETAIL_COLUMNS.map((column) => (
            <th
              key={column.id}
              scope="col"
              aria-sort={ariaSort(column.id)}
              className={`${HEADER_CLASS} ${column.numeric ? 'text-right' : 'text-left'}`}
            >
              <button type="button" onClick={() => onSort(column.id)} className="min-h-11 font-bold tracking-wide uppercase">
                {column.label}
              </button>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.key} className="border-b border-line last:border-b-0">
            <th scope="row" className={`${CELL_CLASS} text-left font-semibold`}>{dateLabel(row.date)}</th>
            <td className={CELL_CLASS}>{row.team}</td>
            <td className={CELL_CLASS}>{row.shift}</td>
            <td className={CELL_CLASS}>{row.assignment}</td>
            <td className={`${CELL_CLASS} text-right`}>{row.required ?? 'None'}</td>
            <td className={`${CELL_CLASS} text-right`}>{row.scheduled}</td>
            <td className={`${CELL_CLASS} text-right`}>{row.available}</td>
            <td className={`${CELL_CLASS} text-right ${percentClass(row.fillPercent)}`}><PercentValue percent={row.fillPercent} /></td>
            <td className={`${CELL_CLASS} text-right`}>{row.onLeave}</td>
            <td className={`${CELL_CLASS} text-right`}>{row.detailedOut}</td>
            <td className={CELL_CLASS}>{row.unfilledRole ? 'Yes' : 'No'}</td>
            <td className={CELL_CLASS}>{row.qualificationGap ? 'Yes' : 'No'}</td>
            <td className={`${CELL_CLASS} font-semibold ${STATUS_CLASS[row.status]}`}>{COVERAGE_REPORT_STATUS_LABEL[row.status]}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
